import { Injectable } from '@angular/core';

import { isNumber, isObject } from 'lodash';
import moment from 'moment';
import * as momentTimezone from 'moment-timezone';
import { Subject } from 'rxjs';

import { FilterButtonType } from '@components/report-filters/models/filterButton.model';
import { IFilter } from '@components/report-filters/report-filter.component';
import { BUSINESS_DATE_FORMAT, SELECT_ALL } from '@config/global-constants';
import { environment } from '@environments/environment';
import { ALLOWED_STACK_LIST_FOR_DOMAINS } from '@services/constants';
import { CustomHttpClient } from '@services/http-client';
import { PreferencesService } from '@services/preferences/preferences.service';
import { IPublishedReport, IReport } from '@shared/types';

@Injectable()
export class FiltersService {
    public externalSubmit$: Subject<void> = new Subject<void>();
    public buttonActionEmitter$: Subject<FilterButtonType> = new Subject<FilterButtonType>();
    constructor(private preferences: PreferencesService) {}

    public getFilters(reportState) {
        if (!reportState) {
            return;
        }

        const filters = [];

        if (reportState.selectedReport?.path && reportState.selectedReport?.additional_path_parameters?.length) {
            for (const parameter of reportState.selectedReport.additional_path_parameters) {
                const res = {};
                res['value'] = null;
                for (const filter of reportState.reportFilters) {
                    if (filter.name === parameter.name && filter.type === parameter.type) {
                        res['value'] = filter.value;
                    }
                }
                res['name'] = parameter.name;
                res['type'] = parameter.type;
                res['label'] = parameter.label;
                res['attributes'] = parameter.attributes;
                res['dependencies'] = parameter.dependencies;
                res['limitations'] = parameter.limitations;

                switch (res['type']) {
                    case 'view':
                    case 'select':
                        res['data'] = typeof parameter['data'] !== 'undefined' ? parameter['data'] : null;
                        res['source'] = typeof parameter['source'] !== 'undefined' ? parameter['source'] : null;
                        res['multiple'] = typeof parameter['multiple'] !== 'undefined' ? parameter['multiple'] : false;
                        res['tooltip'] = typeof parameter['tooltip'] !== 'undefined' ? parameter['tooltip'] : false;
                        res['tooltip-info'] =
                            typeof parameter['tooltip-info'] !== 'undefined' ? parameter['tooltip-info'] : null;
                        res['required'] = parameter['required'] !== undefined ? parameter['required'] : true;
                        res['placeholder'] = parameter.placeholder;
                        break;
                    case 'above-between-below':
                        res['data'] = typeof parameter['data'] !== 'undefined' ? parameter['data'] : null;
                        break;
                    case 'calendars':
                        res['min_date'] = typeof parameter['min_date'] !== 'undefined' ? parameter['min_date'] : null;
                        res['calendar_type'] =
                            typeof parameter['calendar_type'] !== 'undefined' ? parameter['calendar_type'] : null;
                        res['calendar_options'] =
                            typeof parameter['calendar_options'] !== 'undefined' ? parameter['calendar_options'] : null;
                        res['default_date'] =
                            typeof parameter['default_date'] !== 'undefined' ? parameter['default_date'] : null;
                        res['business_date_category'] =
                            typeof parameter['business_date_category'] !== 'undefined'
                                ? parameter['business_date_category']
                                : null;
                        res['multipleSites'] =
                            reportState.selectedReport.additional_path_parameters.filter(
                                (param) => param.type === 'sites',
                            )[0]?.multiple || false;
                        res['multipleEvents'] =
                            reportState.selectedReport.additional_path_parameters.filter(
                                (param) => param.type === 'events',
                            )[0]?.multiple || false;
                        res['use_ordering_date'] =
                            parameter['use_ordering_date'] !== undefined ? parameter['use_ordering_date'] : false;
                        res['use_start_time'] =
                            parameter['use_start_time'] !== undefined ? parameter['use_start_time'] : false;
                        res['enable_min_date_check'] = parameter['enable_min_date_check'] ?? false;
                        break;
                    case 'items':
                        res['multiple'] = parameter['multiple'];
                        break;
                    case 'sites':
                        res['multiple'] = parameter['multiple'];
                        res['site_type'] = parameter['site_type'] || 'All';
                        res['business_types'] = parameter['business_types'];
                        break;
                    case 'configurable-sites':
                        res['multiple'] = parameter['multiple'];
                        res['site_type'] = parameter['site_type'] || 'All';
                        res['business_types'] = parameter['business_types'];
                        res['default_value'] = parameter['default_value'];
                        res['comp_key'] = parameter['comp_key'];
                        res['required'] = parameter['required'] !== undefined ? parameter['required'] : true;
                        break;
                    case 'events':
                        res['multiple'] = parameter['multiple'];
                        res['default_value'] = parameter['default_value'];
                        res['required'] = parameter['required'] !== undefined ? parameter['required'] : true;
                        res['data_filter'] = parameter['data_filter'] || '';
                        res['default_sort'] = parameter['default_sort'] || 'name asc';
                        res['error_key'] = parameter['error_key'] || '';
                        res['enable_min_date_check'] = parameter['enable_min_date_check'] ?? false;
                        break;
                    case 'employees':
                        res['default_value'] = parameter['default_value'];
                        res['multiple'] = parameter['multiple'];
                        res['multipleSites'] =
                            reportState.selectedReport.additional_path_parameters.filter(
                                (param) => param.type === 'sites',
                            )[0]?.multiple || false;
                        res['multipleEvents'] =
                            reportState.selectedReport.additional_path_parameters.filter(
                                (param) => param.type === 'events',
                            )[0]?.multiple || false;
                        break;
                    case 'string':
                        res['placeholder'] =
                            typeof parameter['placeholder'] !== 'undefined' ? parameter['placeholder'] : null;
                        break;
                    case 'combined-select':
                        res['data'] = typeof parameter['data'] !== 'undefined' ? parameter['data'] : null;
                        res['default_value'] = parameter['default_value'] || '';
                        res['show_name_option'] =
                            parameter['show_name_option'] !== undefined ? parameter['show_name_option'] : true;
                        break;
                    case 'destinations':
                        res['multipleSites'] =
                            reportState.selectedReport.additional_path_parameters.filter(
                                (param) => param.type === 'sites',
                            )[0]?.multiple || false;
                        break;
                    case 'day-parts':
                        res['multipleSites'] =
                            reportState.selectedReport.additional_path_parameters.filter(
                                (param) => param.type === 'sites',
                            )[0]?.multiple || false;
                        break;
                    default:
                        res['errors'] = typeof parameter['errors'] !== 'undefined' ? parameter['errors'] : [];
                }

                filters.push(res);
            }
        }
        return filters;
    }

    public getDefaultPeriod(calendars, type, prevInputValue: any | null = null) {
        if (!calendars.length && !type) {
            const today = new Date();
            return { dateStart: today, dateEnd: today };
        }

        if (prevInputValue && prevInputValue.value) {
            if (
                calendars.find((calendar) => {
                    return (
                        moment(calendar.dateStart, BUSINESS_DATE_FORMAT).isSame(prevInputValue.value.dateStart) &&
                        moment(calendar.dateEnd, BUSINESS_DATE_FORMAT).isSame(prevInputValue.value.dateEnd)
                    );
                })
            ) {
                return prevInputValue.value;
            }
        }

        let defaultValue;

        if (type === 'current_period') {
            defaultValue = calendars.find((item) =>
                moment().isBetween(
                    item.dateStart,
                    moment(item.dateEnd, BUSINESS_DATE_FORMAT).subtract(1, 'd'),
                    'day',
                    '[]',
                ),
            );
        }

        if (type === 'previous_period') {
            let index: number | null = null;
            calendars.find((item, i) => {
                if (
                    moment().isBetween(
                        item.dateStart,
                        moment(item.dateEnd, BUSINESS_DATE_FORMAT).subtract(1, 'd'),
                        'day',
                        '[]',
                    )
                ) {
                    index = i - 1;
                }
            });
            if (isNumber(index)) {
                defaultValue = calendars[index];
            }
        }

        if (type === 'this_month') {
            const startDate = moment().startOf('month').toDate();
            const endDate = moment().toDate();
            defaultValue = {
                dateStart: startDate,
                dateEnd: endDate,
            };
        }

        if (type === 'this_year') {
            const startDate = moment().startOf('year').toDate();
            const endDate = moment().toDate();
            defaultValue = {
                dateStart: startDate,
                dateEnd: endDate,
            };
        }

        if (defaultValue) {
            defaultValue = {
                ...defaultValue,
            };
        } else {
            defaultValue = {
                ...calendars[calendars.length - 1],
            };
        }

        return defaultValue;
    }

    public setDefaultFilters(report, defaultFilers = null): IFilter[] {
        const results = [];
        if (report && report.additional_path_parameters) {
            const dateStart = 'Start_Date';
            const dateStop = 'End_Date';

            report.additional_path_parameters.forEach((param) => {
                const values = {};
                switch (param.type) {
                    case 'calendars':
                        values[dateStart] = defaultFilers?.[param.type]?.Start_Date || '';
                        values[dateStop] = defaultFilers?.[param.type]?.End_Date || '';
                        values['calendar'] = '';
                        values['business_date_category'] = param['business_date_category'] || 'Payroll';
                        results.push({
                            name: 'calendars',
                            type: 'calendars',
                            value: values,
                        });
                        break;
                    case 'sites':
                        values['Site_Id'] = defaultFilers ? { value: defaultFilers?.[param.type] } : [];
                        values['exclude_site_id'] = [];
                        results.push({
                            name: 'sites',
                            type: 'sites',
                            value: values,
                        });
                        break;
                    case 'configurable-sites':
                        values['Site_Id'] = defaultFilers ? { value: defaultFilers?.[param.type] } : [];
                        results.push({
                            name: 'configurable-sites',
                            type: 'configurable-sites',
                            value: values,
                        });
                        break;
                    case 'view':
                    case 'select':
                        switch (param.name) {
                            case 'sites':
                                values['Site_Id'] = '';
                                results.push({
                                    name: 'sites',
                                    type: 'select',
                                    value: values,
                                });
                                break;
                            default:
                                if (
                                    param['default_value'] ||
                                    param['default_value'] === false ||
                                    param['default_value'] === 0
                                ) {
                                    values[param['attributes'][0]] =
                                        defaultFilers?.[param.name] || param['default_value'];
                                    results.push({
                                        name: param.name,
                                        type: param.type,
                                        value: values,
                                    });
                                }
                                break;
                        }
                        break;
                    case 'checkbox':
                        if (
                            param['default_value'] ||
                            param['default_value'] === false ||
                            param['default_value'] === 0
                        ) {
                            values[param['attributes'][0]] = param['default_value'];
                            results.push({
                                name: param.name,
                                type: param.type,
                                value: values,
                            });
                        }
                        break;
                    case 'site':
                        values['Site_Id'] = '';
                        results.push({
                            name: 'site',
                            type: 'site',
                            value: values,
                        });
                        break;
                    case 'job-categories':
                        values['Job_Category'] = SELECT_ALL;
                        results.push({
                            name: 'job-categories',
                            type: 'job-categories',
                            value: values,
                        });
                        break;
                    case 'destinations':
                        results.push({
                            name: param.name,
                            type: param.type,
                            value: param.default_value,
                        });
                        break;
                    case 'day-parts':
                        results.push({
                            name: param.name,
                            type: param.type,
                            value: param.default_value,
                        });
                        break;
                    case 'employees':
                        values['Employees'] = SELECT_ALL;
                        results.push({
                            name: 'employees',
                            type: 'employees',
                            value: values,
                        });
                        break;
                    case 'items':
                        values['Item_Dept'] = SELECT_ALL;
                        values['Item_Type'] = SELECT_ALL;

                        results.push({
                            name: 'items',
                            type: 'items',
                            value: values,
                        });
                        break;
                    case 'above-between-below':
                        values['Range'] = 'All';
                        results.push({
                            name: param.name,
                            type: param.type,
                            value: values,
                        });
                        break;
                    case 'static-params':
                        if (param.attributes.length > 0) {
                            param.attributes.forEach((attr) => {
                                values[attr['name']] = attr['value'];
                            });
                        }
                        results.push({
                            name: 'static-params',
                            type: 'static-params',
                            value: values,
                        });
                        break;
                    case 'combined-select':
                        if (param.attributes.length > 0 && param['default_value']) {
                            values[param['attributes'][0]] = param['default_value'];

                            param.attributes.forEach((attr) => {
                                values[attr['param_name']] = attr;
                            });
                        }
                        results.push({
                            name: 'combined-select',
                            type: 'combined-select',
                            value: values,
                        });
                        break;
                    case 'events':
                        values['Event_Id'] = SELECT_ALL;
                        results.push({
                            name: 'events',
                            type: 'events',
                            value: values,
                        });
                        break;
                    default: {
                        // no handling required for other filter types
                    }
                }
            });
        }
        return results;
    }

    /**
     * prepare params for ag-grid reports
     * @returns {Array}
     */
    public prepareParamsForCustomReports(filterValues: Array<IFilter>, selectedReport: IReport | IPublishedReport) {
        const results = {};
        for (const filter of filterValues) {
            const keys = Object.keys(filter['value']);
            for (const k of keys) {
                let value;
                if (['Site_Id', 'Exclude_Site_Id', 'Event_Id', 'From_Site_Id', 'To_Site_Id'].includes(k)) {
                    value = filter['value'][k].value;
                } else {
                    value = filter['value'][k];
                    if (k === 'Group_Option') {
                        if (value === 'By_Hierarchy' || value === 'By_Hierarchy_Site') {
                            const siteHierarchyId = this.preferences.siteHierarchyId;
                            results['Site_Hierarchy_Id'] = `Site_Hierarchy_Id=${siteHierarchyId || 'None'}`;
                        }
                    }
                }
                if (typeof value === 'object') {
                    if (Array.isArray(value)) {
                        results[k] = value.map((item) => (typeof item === 'object' ? item.id : item));
                    }
                } else {
                    results[k] = value;
                }
            }
        }
        if (selectedReport.custom_report_title) {
            results['custom_title'] = selectedReport.custom_report_title;
        }

        if (selectedReport._id) {
            results['_id'] = selectedReport._id;
        }

        return results;
    }

    private prepareFilterValue(val) {
        if (typeof val === 'object') {
            return escape(val['id']);
        } else {
            return escape(val);
        }
    }

    /**
     * prepare url for ssrs
     * @returns {string}
     */
    public prepareSSRSUri(
        filterValues: Array<IFilter>,
        selectedReport: IReport,
        http: CustomHttpClient,
        hierarchyId: string,
        timeLocale,
        reportGroupingMethod: string,
        theme: string,
    ) {
        const tz = momentTimezone.tz.guess();
        let result = '';
        if (selectedReport) {
            let path = encodeURI(selectedReport.path);
            path = this.correctPBIPathIfNecessary(path, http);
            const authorization = http.getAccessToken();
            const additionalParameters = this.getSSRSFilterParameters(
                filterValues,
                hierarchyId,
                reportGroupingMethod,
                theme,
            );
            result = `${path}&rs:embed=true&rc:parameters=false&token=${authorization}&TZ=${tz}`;
            if (selectedReport.custom_report_title) {
                result = `${result}&Title=${selectedReport.custom_report_title}`;
            }

            if (timeLocale) {
                result = `${result}&Locale=${timeLocale}`;
            }

            if (additionalParameters) {
                result = `${result}&${additionalParameters}`;
            }
        }
        return result;
    }

    private correctPBIPathIfNecessary(path: string, http) {
        if (ALLOWED_STACK_LIST_FOR_DOMAINS.includes(environment.envName)) {
            const curDomain = path.split('.')[1];
            let domain = http.getHostname();

            // for local development
            if (domain === 'localhost') {
                return path;
            }

            domain = domain.split('.')[1];
            if (curDomain !== domain) {
                return path.replace(curDomain, domain);
            }
        }
        return path;
    }

    /**
     * set filter params for ssrs custom-reports
     * @returns {string}
     */
    private getSSRSFilterParameters(
        filterValues: Array<IFilter>,
        hierarchyId: string,
        reportGroupingMethod: string,
        theme: string,
    ) {
        const results = [];
        for (const filter of filterValues) {
            if (!isObject(filter['value'])) {
                return;
            }
            const keys = Object.keys(filter['value']);
            for (const k of keys) {
                let value;
                if (['Site_Id', 'Exclude_Site_Id', 'Event_Id'].includes(k)) {
                    value = filter['value'][k].value;
                } else {
                    value = filter['value'][k];
                    if (k === 'Group_Option') {
                        if (value === 'By_Hierarchy' || value === 'By_Hierarchy_Site') {
                            const hierarchy = `Site_Hierarchy_Id=${hierarchyId ? hierarchyId : 'None'}`;
                            results.push(hierarchy);
                        }
                    }
                }
                if (typeof value === 'object') {
                    for (const j of value) {
                        if (this.isUneededParamsForSSRS(j)) {
                            results.push(k + '=' + this.prepareFilterValue(j));
                        }
                    }
                } else {
                    if (this.isUneededParamsForSSRS(k)) {
                        results.push(k + '=' + this.prepareFilterValue(value));
                    }
                }
            }
        }
        results.push('reportGroupingMethod=' + reportGroupingMethod);
        results.push('Theme=' + theme);
        return results.join('&');
    }

    private isUneededParamsForSSRS(attr) {
        const params = ['dateRange', 'calendar'];
        for (const param of params) {
            if (attr === param) {
                return false;
            }
        }
        return true;
    }

    public submitFiltersExternally(): void {
        this.externalSubmit$.next();
    }

    public emitButtonAction(action: FilterButtonType): void {
        this.buttonActionEmitter$.next(action);
    }
}
